import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Loader2, Mail, Lock, KeyRound } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/PasswordInput";
import { Label } from "../../components/ui/label";
import { SEG_TOKEN_KEY } from "../../lib/segApi";
import { ClienteInstallButton } from "../clientearea/ClienteInstallButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SegnalatoreLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const fmtErr = (d) => (typeof d === "string" ? d : "Operazione non riuscita. Riprova.");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/segnalatore/login`, { email, password });
      setChallengeId(data.challenge_id);
      setStep("otp");
      setCode("");
      setInfo(`Abbiamo inviato un codice a ${data.email}`);
    } catch (e2) {
      setErr(fmtErr(e2.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/segnalatore/otp/verify`, { challenge_id: challengeId, code });
      localStorage.setItem(SEG_TOKEN_KEY, data.token);
      navigate("/segnalatori/area", { replace: true });
    } catch (e2) {
      setErr(fmtErr(e2.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10 pgp-grid-bg">
      <div className="w-full max-w-sm pgp-fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <Shield className="h-5 w-5 text-[#0a0d14]" strokeWidth={2.4} />
          </div>
          <span className="font-head font-extrabold text-lg">Patro<span className="text-amber-500">Gest</span>Pro</span>
        </div>

        <h2 className="font-head text-2xl font-bold text-slate-50">Area Segnalatori</h2>
        <p className="text-sm text-slate-400 mt-1 mb-5">
          {step === "otp" ? "Inserisci il codice ricevuto via email." : "Accedi con l'email e la password fornite dallo studio."}
        </p>

        {step === "credentials" ? (
          <form onSubmit={submit} className="space-y-4" data-testid="seg-login-form">
            <div>
              <Label className="text-slate-300 text-sm">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tuaemail@esempio.it" required data-testid="seg-login-email" className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="seg-login-password" className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11" />
              </div>
            </div>
            {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="seg-login-error">{err}</div>}
            <Button type="submit" disabled={loading} data-testid="seg-login-submit" className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="space-y-4" data-testid="seg-otp-form">
            {info && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2" data-testid="seg-otp-info">{info}</div>}
            <div>
              <Label className="text-slate-300 text-sm">Codice di verifica</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" required autoFocus data-testid="seg-otp-code" className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11 tracking-[0.4em] font-mono text-lg" />
              </div>
            </div>
            {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="seg-login-error">{err}</div>}
            <Button type="submit" disabled={loading || code.length < 6} data-testid="seg-otp-submit" className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verifica e accedi"}
            </Button>
            <button type="button" onClick={() => { setStep("credentials"); setErr(""); setInfo(""); }} data-testid="seg-otp-back" className="text-slate-400 hover:text-slate-200 text-sm">← Indietro</button>
          </form>
        )}
        <ClienteInstallButton p="seg" />
      </div>
    </div>
  );
}
